// @ts-strict-ignore
import React, {
  useEffect, useMemo, useContext, useCallback,
  useRef, useState,
} from 'react';
import styled from 'styled-components';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  ButtonVariant,
  Checkbox,
  DatePicker,
  Icon,
  IconName,
  Modal,
  MultiSelect,
  RadioGroup,
  Select,
  Text,
  TextArea,
  Tooltip,
} from '@usealloy/component-library';
import { Controller, useForm } from 'react-hook-form';
import { UserContext } from '~/dashboard/context';
import type { Option } from '~/journey/DeveloperGuide/types';
import {
  emptyFormState,
  getPopulatedFormState,
  getDefaultValueNumberError,
  getDefaultValueDateError,
  getDefaultValueStringError,
  validateValueRanges,
} from './utils';
import type { PublishedAttributeFormatted, PublishedAttributeGroupingFormatted } from '../types';

import AcceptedValuesField from './AcceptedValuesField';
import ValidValuesField from './ValidValuesField';
import { StyledDatePickerWrapper, StyledErrorText, StyledTextField } from './styles';
import { outputCategoryOptions, OUTPUT_CATEGORY_RISK_SCORING } from '../constants';
import { ValueRangeConfigTable, ReadOnlyValueRangeConfig } from '~/components/ValueRangeConfig';
import { ValueRangesConfirmationModal } from '~/components/ValueRangeConfig/ValueRangesConfirmationModal';
import type { ValueRangeConfigOptions } from 'flow/store/engine/types';
import { LowRangeOption, HighRangeOption } from 'flow/service/actions/matrixModels';

const StyledFormWrapper = styled.div`
  & > :not(:last-child) {
    margin-bottom: var(--cl-space-4);
  }
`;

export const getAgentUpdateValueTooltipText = (objectType: string) => {
  const objectTypeName = objectType === 'transaction' ? 'Transaction' : 'Entity';
  const possessiveForm = objectType === 'transaction' ? "Transaction's" : "Entity's";

  return `
  If selected, Agents with the proper permissions can update values on this Published Attribute.
  This will override any existing values for this attribute on the ${objectTypeName}.
  All values will show on the ${possessiveForm} Published Attribute History.
`;
};

export type AddPublishedAttributeModalProps = {
  showModal: boolean;
  setShowModal: (shouldShow: boolean) => void;
  addAttribute: (newAttribute: PublishedAttributeFormatted) => void;
  editAttribute: (updatedAttributeValues: Partial<PublishedAttributeFormatted>) => void;
  attributeToEdit: PublishedAttributeFormatted | null;
  setAttributeToEdit: (attributeToEdit: PublishedAttributeFormatted | null) => void;
  attributeError: any;
  attributeGroupings?: PublishedAttributeGroupingFormatted[];
  attributeGroupingsLoading?: boolean;
};

const AddPublishedAttributeModal = ({
  showModal,
  setShowModal,
  addAttribute,
  editAttribute,
  attributeToEdit,
  setAttributeToEdit,
  attributeError,
  attributeGroupings = [],
  attributeGroupingsLoading = false,
}: AddPublishedAttributeModalProps) => {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    trigger,
    formState: {
      errors,
      isDirty,
      isValid,
    },
  } = useForm({
    defaultValues: emptyFormState,
    mode: 'onTouched',
  });

  const { agent, featureFlags } = useContext(UserContext);
  const valueRangeConfigRef = useRef<HTMLDivElement>(null);
  const [showValueRangesModal, setShowValueRangesModal] = useState(false);

  const canSetCraRangeFromPASettings = Boolean(featureFlags?.customerRiskAssessmentCra)
    && Boolean(featureFlags?.craConfigPas);


  const isAlloyAdmin = agent.isEmployee;

  const objectType = watch('objectType');
  const dataType = watch('dataType');
  const defaultValue = watch('defaultValue');
  const agentCanCreateValue = watch('agentCanCreateValue');
  const category = watch('category');
  const defineValueRanges = watch('defineValueRanges');
  const valueRangeConfig = watch('valueRangeConfig');
  const valueRangeConfigOptions = watch('valueRangeConfigOptions');
  const {
    onChange: onChangeAgentCanCreateValue,
    ...registerAgentCanCreateValue
  } = register('agentCanCreateValue');
  const {
    onChange: onChangeContainsPii,
    ...registerContainsPii
  } = register('containsPii');
  const containsPii = watch('containsPii');
  const {
    onChange: onChangeHasExpiration,
    ...registerHasExpiration
  } = register('hasExpiration');
  const hasExpiration = watch('hasExpiration');
  const {
    onChange: onChangeHasJqFilter,
    ...registerHasJqFilter
  } = register('hasJqFilter');
  const hasJqFilter = watch('hasJqFilter');
  const {
    onChange: onChangeDefineValueRanges,
    ...registerDefineValueRanges
  } = register('defineValueRanges');

  const resetForm = () => {
    setAttributeToEdit(null);
    reset(emptyFormState);
  };

  const updateValueRangeConfigOptions = useCallback(
    (value: ValueRangeConfigOptions) => { setValue('valueRangeConfigOptions', value); },
    [setValue],
  );

  const handleDefineValueRangesChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = evt.target;
    if (!checked && defineValueRanges) {
      // Unchecking - show confirmation modal
      setShowValueRangesModal(true);
    } else {
      // Checking - allow immediately
      setValue('defineValueRanges', checked);
      onChangeDefineValueRanges(evt);
    }
  };

  const handleConfirmRemoveValueRanges = () => {
    setValue('defineValueRanges', false, { shouldDirty: true });
    setValue('valueRangeConfig', [], { shouldDirty: true });
    setValue('valueRangeConfigOptions', {
      lowRange: LowRangeOption.bounded,
      highRange: HighRangeOption.bounded,
    }, { shouldDirty: true });
    setShowValueRangesModal(false);
  };

  const onSubmitAdd = data => {
    const payload = {
      ...data,
      hasJqFilter,
    };

    addAttribute(payload);
  };

  const onSubmitEdit = data => {
    const payload = {
      ...attributeToEdit,
      ...data,
      hasJqFilter,
    };

    editAttribute(payload);
  };

  const defaultValueComponent = useMemo(() => {
    switch (dataType) {
      case 'date':
      case 'timestamp':
        return (
          <Box justifyContent="flex-start" direction="column" alignItems="flex-start">
            <Text style={{ marginBottom: 'var(--cl-space-1)' }}>
              <span style={{ color: 'var(--cl-background-danger)' }}>*</span>
              Default Value
            </Text>
            <Controller
              control={control}
              name="defaultValue"
              rules={{
                required: 'Please enter a default value.',
                validate: (value, { validValueMin, validValueMax }) => {
                  if (featureFlags.paValidationImprovements) {
                    return (
                      getDefaultValueDateError(value, validValueMin, validValueMax)
                    );
                  }
                  return null;
                },
                deps: featureFlags?.paValidationImprovements
                  ? ['validValueMax', 'validValueMin']
                  : [],
              }}
              render={({
                field: {
                  value, onChange, ref: fieldRef, ...field
                },
              }) => {
                const dateFormat = dataType === 'date'
                  ? 'YYYY-MM-DD'
                  : 'YYYY-MM-DDTHH:mm:ss[Z]';

                return (
                  <StyledDatePickerWrapper>
                    <DatePicker
                      {...field}
                      ref={fieldRef}
                      value={value ? dayjs(value, dateFormat) : null}
                      showTime={dataType === 'timestamp'}
                      onChange={(date: Dayjs) => {
                        onChange(date.format(dateFormat));
                      }}
                      style={{ width: '100%' }}
                      status={errors.defaultValue ? 'error' : ''}
                    />
                  </StyledDatePickerWrapper>
                );
              }}
            />
            {errors.defaultValue?.message && (
              <Box justifyContent="flex-start" style={{ marginTop: 'var(--cl-space-1)' }}>
                <StyledErrorText>
                  {errors.defaultValue?.message}
                </StyledErrorText>
              </Box>
            )}
          </Box>
        );
      case 'boolean':
        return (
          <Box justifyContent="flex-start" direction="column" alignItems="flex-start">
            <Text style={{ marginBottom: 'var(--cl-space-2)' }}>
              <span style={{ color: 'var(--cl-background-danger)' }}>*</span>
              Default Value
            </Text>
            <RadioGroup
              value={defaultValue}
              style={{ flexDirection: 'row' }}
              onValueChange={value => {
                setValue('defaultValue', value, { shouldDirty: true });
              }}
            >
              <RadioGroup.Item
                label="True"
                value="true"
                {...register('defaultValue', { required: 'Please enter a default value.' })}
              />
              <RadioGroup.Item
                label="False"
                value="false"
                {...register('defaultValue', { required: 'Please enter a default value.' })}
              />
            </RadioGroup>
          </Box>
        );
      case 'integer':
      case 'decimal':
        return (
          <StyledTextField
            type="number"
            step={dataType === 'integer' ? 1 : 0.1}
            label="Default Value"
            error={errors.defaultValue?.message}
            {...register('defaultValue', {
              required: 'Please enter a default value.',
              onBlur: ({ target: { value } }) => {
                if (value && dataType === 'integer') {
                  setValue('defaultValue', parseInt(value, 10).toString(), { shouldDirty: true });
                }
              },
              validate: (value, { validValueMin, validValueMax }) => {
                if (featureFlags.paValidationImprovements) {
                  return (
                    getDefaultValueNumberError(value, validValueMin, validValueMax, dataType)
                  );
                }
                return null;
              },
              deps: featureFlags?.paValidationImprovements
                ? ['validValueMax', 'validValueMin']
                : [],
            })}
            required
          />
        );
      default:
        return (
          <StyledTextField
            type="text"
            label="Default Value"
            placeholder={dataType === 'string' ? '(empty string)' : ''}
            error={errors.defaultValue?.message}
            // Must override the registered options set by the integer/decimal field
            {...register('defaultValue', {
              required: false,
              onBlur: () => {},
              validate: (value, { validValues }) => {
                if (featureFlags.paValidationImprovements) {
                  return (
                    getDefaultValueStringError(value, validValues)
                  );
                }
                return null;
              },
              deps: featureFlags?.paValidationImprovements
                ? ['validValues']
                : [],
            })}
          />
        );
    }
  }, [dataType, defaultValue, errors.defaultValue?.message]);

  const saveButton = (
    <Button
      variant={ButtonVariant.primary}
      onClick={handleSubmit(attributeToEdit ? onSubmitEdit : onSubmitAdd)}
      disabled={showValueRangesModal || (attributeToEdit ? !isValid || !isDirty : !isValid)}
    >
      Save
    </Button>
  );

  const cancelButton = (
    <Button
      variant={ButtonVariant.secondary}
      onClick={() => {
        setShowModal(false);
        resetForm();
      }}
      disabled={showValueRangesModal}
    >
      Cancel
    </Button>
  );

  useEffect(() => {
    const isLastRowTouched = valueRangeConfig?.length > 0 && (
      valueRangeConfig[valueRangeConfig.length - 1]?.maxValue !== null
      || valueRangeConfig[valueRangeConfig.length - 1]?.color !== ''
      || valueRangeConfig[valueRangeConfig.length - 1]?.label !== ''
    );
    if (isLastRowTouched
      && valueRangeConfigRef.current
      && !valueRangeConfigRef.current?.contains(document.activeElement)
    ) {
      trigger('valueRangeConfig');
    }
  }, [valueRangeConfig, trigger]);

  useEffect(() => {
    if (Boolean(attributeToEdit) || !showModal) {
      return;
    }

    resetForm();
  }, [showModal, attributeToEdit]);

  useEffect(() => {
    if (!attributeToEdit) {
      return;
    }

    const formState = getPopulatedFormState(attributeToEdit);
    reset(formState);
  }, [attributeToEdit]);

  // Auto-set defineValueRanges when category is set to Risk Scoring
  useEffect(() => {
    if (category === OUTPUT_CATEGORY_RISK_SCORING) {
      setValue('defineValueRanges', true);
    }
  }, [category, setValue]);


  useEffect(() => {
    if (!attributeError) {
      return;
    }

    if (attributeError?.details?.publishedAttributeName) {
      setError('attributeName', { type: 'custom', message: attributeError.message });
    }

    if (attributeError?.details?.defaultValue) {
      setError('defaultValue', { type: 'custom', message: attributeError.message });
    }
  }, [attributeError]);

  const groupingOptions = useMemo(() => (
    attributeGroupings
      .filter(attribute => !attribute.archived)
      .map(({ groupingName, groupingToken }) => ({
        label: groupingName,
        value: groupingToken,
      }))
  ), [attributeGroupings]);

  // valueRangeConfig is behind a feature flag and a checkbox, and is read-only once it is set
  // These three booleans check the form logic to determine if we want to show the config,
  // And if there is an existing valueRangeConfig (and we should show the read-only version)
  const shouldShowValueRangeConfig = Boolean(
    canSetCraRangeFromPASettings
    && defineValueRanges);
  const shouldShowReadOnlyValueRangeConfig = shouldShowValueRangeConfig && Boolean(
    attributeToEdit && attributeToEdit.valueRangeConfig,
  );
  const shouldShowEditValueRangeConfig = shouldShowValueRangeConfig
    && !shouldShowReadOnlyValueRangeConfig;

  return (
    <Modal
      isOpen={showModal}
      header={`${attributeToEdit ? 'Configure' : 'Add'} Published Attribute`}
      primaryAction={saveButton}
      secondaryAction={cancelButton}
      autoFocus={false}
      onClose={() => {
        setShowModal(false);
        resetForm();
      }}
    >
      <StyledFormWrapper>
        {featureFlags?.customTransactionAttributes && isAlloyAdmin && (
          <Select
            label="Object"
            placeholder="Select One"
            options={[
              { name: 'Entity', value: 'entity' },
              { name: 'Transaction', value: 'transaction' },
            ]}
            error={errors.objectType?.message}
            {...register('objectType', { required: 'Please select an object type.' })}
            required
            disabled={Boolean(attributeToEdit)}
            onChange={event => {
              const newObjectType = event.target.value;
              setValue('objectType', newObjectType, { shouldDirty: true });
              // Clear grouping when switching to transaction
              if (newObjectType === 'transaction') {
                setValue('groupingToken', null, { shouldDirty: true });
              }
            }}
          />
        )}
        <StyledTextField
          type="text"
          label="Attribute Name"
          error={errors.attributeName?.message}
          {...register('attributeName', { required: 'Please enter an attribute name.' })}
          required
        />
        <TextArea
          label="Description"
          rows={2}
          {...register('description')}
        />
        <Select
          label="Data Type"
          placeholder="Select One"
          options={[
            { name: 'String', value: 'string' },
            { name: 'Integer', value: 'integer' },
            { name: 'Decimal', value: 'decimal' },
            { name: 'Boolean', value: 'boolean' },
            { name: 'Date', value: 'date' },
            { name: 'Timestamp', value: 'timestamp' },
          ]}
          error={errors.dataType?.message}
          {...register('dataType', {
            required: 'Please enter a data type.',
            deps: featureFlags?.paValidationImprovements
              ? ['validValueMax', 'validValueMin', 'defaultValue']
              : [],
          })}
          required
          disabled={Boolean(attributeToEdit)}
          onChange={event => {
            setValue('dataType', event.target.value, { shouldDirty: true });
            setValue('defaultValue', '', { shouldDirty: true });
            if (featureFlags?.paValidationImprovements) {
              setValue('validValueMin', '', { shouldDirty: true });
              setValue('validValueMax', '', { shouldDirty: true });
            }
          }}
        />
        {featureFlags?.groupedPublishedAttributes && (
          <Box justifyContent="flex-start" direction="column" alignItems="flex-start" style={{ width: '100%' }}>
            <Box justifyContent="flex-start" gap={1} alignItems="center" style={{ marginBottom: 'var(--cl-space-1)' }}>
              <Text>Grouping</Text>
              {objectType === 'transaction' && (
                <Tooltip
                  side="top"
                  content="Grouping is not available for Transaction Published Attributes"
                >
                  <Icon icon={IconName.Info} />
                </Tooltip>
              )}
            </Box>
            <Controller
              control={control}
              name="groupingToken"
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  isMulti={false}
                  isSearchable
                  isClearable
                  onChange={(newValue: Option) => {
                    onChange(newValue?.value || null);
                  }}
                  selectedOptions={objectType === 'transaction' ? null : groupingOptions.find(option => option.value === value)}
                  options={groupingOptions}
                  isDisabled={attributeGroupingsLoading || objectType === 'transaction'}
                  placeholder={(() => {
                    if (objectType === 'transaction') {
                      return 'Grouping is not available for transactions';
                    }
                    if (attributeGroupingsLoading) {
                      return 'Published Attribute groupings loading...';
                    }
                    return 'Select a grouping to add your published attribute to';
                  })()}
                />
              )}
            />
          </Box>
        )}
        {canSetCraRangeFromPASettings && (
          <Controller
            control={control}
            name="category"
            render={({ field: { onChange, value } }) => (
              <MultiSelect
                label="Category"
                isClearable
                isMulti={false}
                options={outputCategoryOptions}
                selectedOptions={
                  outputCategoryOptions.find(option => option.value === value) ?? null
                }
                onChange={(newValue: Option) => {
                  onChange(newValue?.value ?? '');
                  if (newValue?.value === OUTPUT_CATEGORY_RISK_SCORING) {
                    setValue('defineValueRanges', true);
                  }
                }}
                isDisabled={Boolean(attributeToEdit && category)}
              />
            )}
          />
        )}
        <Box gap={4} alignItems="flex-start">
          {defaultValueComponent}
          {featureFlags?.paValidationImprovements && (
            <AcceptedValuesField
              control={control}
              dataType={dataType}
              errors={errors}
            />
          )}
        </Box>
        <StyledTextField
          type="text"
          label="Default Value Note"
          {...register('defaultValueNote')}
        />
        {featureFlags?.paValidationImprovements && dataType === 'string' && (
          <ValidValuesField
            control={control}
            errors={errors}
          />
        )}
        <Checkbox
          label="This attribute contains PII"
          checked={containsPii}
          onChange={evt => {
            setValue('containsPii', evt.target.checked);
            onChangeContainsPii(evt);
          }}
          disabled={Boolean(attributeToEdit)}
          {...registerContainsPii}
        />
        <Checkbox
          label="This attribute expires"
          checked={hasExpiration}
          onChange={evt => {
            const { checked } = evt.target;

            setValue('hasExpiration', checked);
            onChangeHasExpiration(evt);
          }}
          {...registerHasExpiration}
        />
        {!!hasExpiration && (
          <StyledTextField
            type="number"
            label="Days Until Expiration"
            placeholder="Number of days"
            error={errors.valueExpiresInDays?.message}
            {...register('valueExpiresInDays', {
              required: 'Please enter an expiration.',
              onBlur: ({ target: { value } }) => {
                const valueExpiresInDays = Math.max(parseInt(value, 10), 0);

                setValue('valueExpiresInDays', valueExpiresInDays.toString(), { shouldDirty: true });
              },
            })}
            required
          />
        )}
        {featureFlags?.paValidationImprovements && (
          <Box justifyContent="flex-start">
            <Checkbox
              label="Agents can update this attribute's values in the dashboard"
              checked={agentCanCreateValue}
              onChange={evt => {
                setValue('agentCanCreateValue', evt.target.checked);
                onChangeAgentCanCreateValue(evt);
              }}
              {...registerAgentCanCreateValue}
            />
            <Tooltip
              side="top"
              content={getAgentUpdateValueTooltipText(objectType)}
            >
              <Icon icon={IconName.Info} />
            </Tooltip>
          </Box>
        )}
        <Checkbox
          label="Parse incoming values with JQ?"
          checked={hasJqFilter}
          onChange={evt => {
            const { checked } = evt.target;

            setValue('hasJqFilter', checked);
            onChangeHasJqFilter(evt);
          }}
          {...registerHasJqFilter}
          helpText="Incoming values will only be parsed when the Published Attribute is used as an Input Attribute. If parsing outbound values, configure the JQ in the workflow builder."
        />
        {!!hasJqFilter && (
          <TextArea
            label="Formula"
            rows={2}
            error={errors.jqFilter?.message}
            {...register('jqFilter', { required: 'Please enter a JQ formula.' })}
            required
          />
        )}
        {canSetCraRangeFromPASettings && (
          <Checkbox
            checked={defineValueRanges}
            onChange={handleDefineValueRangesChange}
            label="Define ranges for values"
            disabled={
              Boolean(category === OUTPUT_CATEGORY_RISK_SCORING)
              || Boolean(attributeToEdit && defineValueRanges)
            }
            helpText={
              category === OUTPUT_CATEGORY_RISK_SCORING
                ? 'You must define ranges for values if the Risk Scoring category is selected.'
                : undefined
            }
            {...registerDefineValueRanges}
          />
        )}
        {shouldShowReadOnlyValueRangeConfig && (
          <ReadOnlyValueRangeConfig
            outputValueRange={valueRangeConfig}
            outputValueRangeOptions={valueRangeConfigOptions}
          />
        )}
        {shouldShowEditValueRangeConfig && (
          <Controller
            control={control}
            name="valueRangeConfig"
            rules={{
              validate: (value, formValues) => {
                if (
                  formValues.category === OUTPUT_CATEGORY_RISK_SCORING
                    && (!value || !Array.isArray(value) || value.length === 0)
                ) {
                  return 'Value ranges are required when Risk Scoring category is selected.';
                }

                if (value && Array.isArray(value) && value.length > 0) {
                  return validateValueRanges(
                    value,
                    formValues.valueRangeConfigOptions,
                  );
                }

                return true;
              },
              deps: ['category', 'defineValueRanges', 'valueRangeConfigOptions'],
            }}
            render={({ field: { onChange }, fieldState: { error: fieldError } }) => (
              <div ref={valueRangeConfigRef}>
                <ValueRangeConfigTable
                  outputValueRange={valueRangeConfig}
                  updateOutputValueRange={onChange}
                  outputValueRangeOptions={valueRangeConfigOptions}
                  updateOutputValueRangeOptions={updateValueRangeConfigOptions}
                />
                {fieldError?.message && (
                <Box justifyContent="flex-start" style={{ marginTop: 'var(--cl-space-1)' }}>
                  <StyledErrorText>
                    {fieldError.message}
                  </StyledErrorText>
                </Box>
                )}
              </div>
            )}
          />
        )}
      </StyledFormWrapper>
      <ValueRangesConfirmationModal
        isOpen={showValueRangesModal}
        onCancel={() => setShowValueRangesModal(false)}
        onConfirm={handleConfirmRemoveValueRanges}
      />
    </Modal>
  );
};

export default AddPublishedAttributeModal;
